import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import Text from './Text';

type Props = {
  trigger: React.ReactNode | ((open: boolean) => React.ReactNode);
  /** Take the callback form to close the menu on picking something. */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  header?: string;
  anchorSide?: 'left' | 'right';
};

export default function DropdownMenu({
  trigger,
  children,
  header,
  anchorSide = 'left',
}: Props) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener('click', onClickOutside, true);
    return () => window.removeEventListener('click', onClickOutside, true);
  }, [open]);

  return (
    <html.div ref={ref} style={Style.positionRelative}>
      <html.div style={styles.trigger} onClick={() => setOpen((v) => !v)}>
        {typeof trigger === 'function' ? trigger(open) : trigger}
      </html.div>
      {open && (
        <html.div style={styles.anchor}>
          <html.div
            style={[
              Style.col,
              styles.panel,
              anchorSide === 'right' && styles.anchorRight,
            ]}
          >
            {header && (
              <html.div style={styles.header}>
                <Text typeface="body.small.medium" color="neutral60">
                  {header}
                </Text>
              </html.div>
            )}
            {typeof children === 'function'
              ? children(() => setOpen(false))
              : children}
          </html.div>
        </html.div>
      )}
    </html.div>
  );
}

const styles = css.create({
  trigger: {
    cursor: 'pointer',
  },
  anchor: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 100,
  },
  panel: {
    position: 'absolute',
    left: 0,
    top: 4,
    minWidth: 200,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    boxShadow: '0px 4px 22px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  anchorRight: {
    left: 'auto',
    right: 0,
  },
  header: {
    paddingInline: 12,
    paddingBlockStart: 8,
    paddingBlockEnd: 4,
  },
});
